import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchArm, postponeArm, setArmed as putArmed, type ArmState } from './api'
import type { Site } from './types'

// warnAhead matches VMS's own warning, so a page opened after the warning went
// out shows it for the same ten minutes.
const warnAhead = 10 * 60_000

// ArmEvent is the named `arm` event on the cluster stream.
export interface ArmEvent {
  site_id: string
  armed: boolean
  by?: 'user' | 'schedule'
}

// ArmPendingEvent is the named `arm_pending` event: a disarmed site about to
// arm itself at `at`.
export interface ArmPendingEvent {
  site_id: string
  at: string
}

// ArmPostponedEvent is the named `arm_postponed` event: someone moved a
// site's auto-arm later.
export interface ArmPostponedEvent {
  site_id: string
  at: string
  by: string
}

// ArmNotice is an auto-arm coming up at a site, or a site the schedule has
// just disarmed.
export type ArmNotice =
  | { kind: 'arming'; siteId: string; at: string }
  | { kind: 'disarmed'; siteId: string }

// noticeKey names a notice for React and for App's remount of the strip.
export function noticeKey(n: ArmNotice): string {
  return n.kind === 'arming' ? `arming|${n.siteId}|${n.at}` : `disarmed|${n.siteId}`
}

// ArmFailure is what a dispatcher asked of a site that VMS did not do.
export type ArmFailure = 'arm' | 'disarm' | 'postpone'

// dismissKey names a dismissed warning by its instant: the same arm time
// arrives written in different zones, and a dismissal keyed on the text came
// back after a reconnect (#248).
const dismissKey = (siteId: string, at: string) => `${siteId}|${Date.parse(at)}`

export interface Arming {
  // state holds the sites whose arm state VMS answered for; a site missing
  // from it has none to show.
  state: Record<string, ArmState>
  notices: ArmNotice[]
  // busy holds the sites with a change in flight, whose controls take no
  // second click: a fast double click otherwise undid itself (#234).
  busy: Set<string>
  // failures holds, per site, the last change that failed, until dismissed or
  // another change starts.
  failures: Record<string, ArmFailure>
  // reasons holds VMS's reason for a failure it refused as impossible.
  reasons: Record<string, string>
  // setArmed and postpone never reject: a failure lands in failures.
  setArmed: (siteId: string, armed: boolean) => Promise<void>
  postpone: (siteId: string, minutes: 30 | 60) => Promise<void>
  dismissFailure: (siteId: string) => void
  dismiss: (notice: ArmNotice) => void
  onArm: (e: ArmEvent) => void
  onArmPending: (e: ArmPendingEvent) => void
  onArmPostponed: (e: ArmPostponedEvent) => void
  refresh: () => void
}

/**
 * Each site's arm state and its upcoming auto-arm, read when the site list
 * changes and kept current by the `arm`, `arm_pending` and `arm_postponed`
 * events App passes in from the stream it already holds open.
 */
export function useArming(sites: Site[]): Arming {
  const [state, setState] = useState<Record<string, ArmState>>({})
  const [pending, setPending] = useState<Record<string, string>>({})
  // Keyed by the arm time too, so a postponed arm's own warning still shows.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [disarmed, setDisarmed] = useState<Set<string>>(new Set())
  const ids = sites.map((s) => s.id).join(',')
  const [readAt, setReadAt] = useState(0)

  // refresh reads every site again. The stream drops what was sent while it
  // was down, so a laptop asleep across an auto-arm woke still showing the
  // site disarmed (#233).
  const refresh = useCallback(() => setReadAt((n) => n + 1), [])

  const clearPending = useCallback((siteId: string) => {
    setPending((prev) => {
      if (!(siteId in prev)) return prev
      const next = { ...prev }
      delete next[siteId]
      return next
    })
  }, [])

  const forgetDisarmed = useCallback((siteId: string) => {
    setDisarmed((prev) => {
      if (!prev.has(siteId)) return prev
      const next = new Set(prev)
      next.delete(siteId)
      return next
    })
  }, [])

  // apply takes what VMS answered for a site as the whole truth: a warning or
  // a "disarmed on schedule" notice it no longer supports goes. A refresh used
  // to clear a warning only when the site read armed, so one postponed
  // elsewhere stayed at "arms now" (#240).
  // keepWarning leaves a warning standing unless the site reads armed: an
  // `arm_pending` event, not this viewer's clock against next, says one is due
  // (#254).
  const apply = useCallback((id: string, a: ArmState, keepWarning = false) => {
    setState((prev) => ({ ...prev, [id]: a }))
    if (!a.armed && a.next?.action === 'arm' && Date.parse(a.next.at) - Date.now() <= warnAhead) {
      const at = a.next.at
      setPending((prev) => ({ ...prev, [id]: at }))
    } else if (a.armed || !keepWarning) {
      clearPending(id)
    }
    if (a.armed) forgetDisarmed(id)
  }, [clearPending, forgetDisarmed])

  // gen counts each site's events and changes. A read sent before one and
  // answered after it put the old state back, and dropped the "disarmed on
  // schedule" notice with it (#251), so such a read is dropped.
  const gen = useRef<Record<string, number>>({})
  const bump = useCallback((id: string) => { gen.current[id] = (gen.current[id] ?? 0) + 1 }, [])

  const readSite = useCallback((id: string, live: () => boolean = () => true, keepWarning = false) => {
    const started = gen.current[id] ?? 0
    return fetchArm(id).then((a) => { if (live() && (gen.current[id] ?? 0) === started) apply(id, a, keepWarning) }).catch(() => {})
  }, [apply])

  useEffect(() => {
    let cancelled = false
    for (const id of ids ? ids.split(',') : []) void readSite(id, () => !cancelled)
    return () => { cancelled = true }
  }, [ids, readAt, readSite])

  // An arm event says only armed or disarmed; the site is read again so its
  // next arm time and who changed it are current, which went stale (#240).
  const onArm = useCallback((e: ArmEvent) => {
    bump(e.site_id)
    setState((prev) => ({ ...prev, [e.site_id]: { ...(prev[e.site_id] ?? { next: null }), armed: e.armed } }))
    if (e.armed) {
      clearPending(e.site_id)
      forgetDisarmed(e.site_id)
    } else if (e.by === 'schedule') {
      setDisarmed((prev) => new Set(prev).add(e.site_id))
    }
    void readSite(e.site_id)
  }, [bump, clearPending, forgetDisarmed, readSite])

  // supersede drops any read of the site now out and asks again: dropped
  // alone, a warning on page load left the site with no switch until a
  // reconnect (#254).
  const supersede = useCallback((id: string) => {
    bump(id)
    void readSite(id, undefined, true)
  }, [bump, readSite])

  const onArmPending = useCallback((e: ArmPendingEvent) => {
    setPending((prev) => ({ ...prev, [e.site_id]: e.at }))
    supersede(e.site_id)
  }, [supersede])

  // armsAt moves a site's next auto-arm to a postponed time, which the site's
  // header otherwise kept showing as the old one (#238).
  const armsAt = useCallback((siteId: string, at: string) => {
    setState((prev) => (prev[siteId] ? { ...prev, [siteId]: { ...prev[siteId], next: { action: 'arm', at } } } : prev))
  }, [])

  // Whoever postponed it, the warning was for a time that no longer stands;
  // VMS warns again ten minutes before the new one.
  const onArmPostponed = useCallback((e: ArmPostponedEvent) => {
    clearPending(e.site_id)
    armsAt(e.site_id, e.at)
    supersede(e.site_id)
  }, [supersede, clearPending, armsAt])

  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [failures, setFailures] = useState<Record<string, ArmFailure>>({})
  const [reasons, setReasons] = useState<Record<string, string>>({})
  // A failure for a site no longer listed is forgotten, not hidden: hidden,
  // it came back with a site added again under the same ID (#254).
  const [listedFor, setListedFor] = useState(ids)
  if (listedFor !== ids) {
    setListedFor(ids)
    const listed = new Set(ids ? ids.split(',') : [])
    const onlyListed = <T,>(prev: Record<string, T>) =>
      Object.keys(prev).every((id) => listed.has(id)) ? prev : Object.fromEntries(Object.entries(prev).filter(([id]) => listed.has(id)))
    setFailures(onlyListed)
    setReasons(onlyListed)
  }
  // inFlight mirrors busy for the click handlers, which a state update has
  // not reached yet when a second click follows the first at once.
  const inFlight = useRef<Set<string>>(new Set())

  // attempt runs one change to a site, at most one at a time, and records it
  // if it fails: a failure used to leave the control as it was with no word,
  // which a dispatcher cannot tell from a missed click (#234).
  const attempt = useCallback(async (siteId: string, failure: ArmFailure, change: () => Promise<void>) => {
    if (inFlight.current.has(siteId)) return
    inFlight.current.add(siteId)
    bump(siteId)
    setBusy(new Set(inFlight.current))
    setFailures((prev) => {
      if (!(siteId in prev)) return prev
      const next = { ...prev }
      delete next[siteId]
      return next
    })
    try {
      await change()
    } catch (e) {
      // Read again: this change dropped any read then out, and the switch
      // kept a state VMS no longer held (#254).
      void readSite(siteId, undefined, true)
      setFailures((prev) => ({ ...prev, [siteId]: failure }))
      const reason = (e as { reason?: string }).reason
      setReasons((prev) => {
        const next = { ...prev }
        if (reason) next[siteId] = reason
        else delete next[siteId]
        return next
      })
    } finally {
      inFlight.current.delete(siteId)
      setBusy(new Set(inFlight.current))
    }
  }, [bump, readSite])

  const setArmed = useCallback((siteId: string, armed: boolean) => attempt(siteId, armed ? 'arm' : 'disarm', async () => {
    await putArmed(siteId, armed)
    onArm({ site_id: siteId, armed, by: 'user' })
  }), [attempt, onArm])

  const postpone = useCallback((siteId: string, minutes: 30 | 60) => attempt(siteId, 'postpone', async () => {
    const answer = await postponeArm(siteId, minutes)
    bump(siteId)
    clearPending(siteId)
    if (answer?.at) armsAt(siteId, answer.at)
  }), [attempt, bump, clearPending, armsAt])

  const dismissFailure = useCallback((siteId: string) => {
    setFailures((prev) => {
      const next = { ...prev }
      delete next[siteId]
      return next
    })
  }, [])

  const dismiss = useCallback((n: ArmNotice) => {
    if (n.kind === 'disarmed') forgetDisarmed(n.siteId)
    else setDismissed((prev) => new Set(prev).add(dismissKey(n.siteId, n.at)))
  }, [forgetDisarmed])

  // Only the sites listed now: a removed site's notice showed its raw id for
  // good (#248).
  const keep = new Set(ids ? ids.split(',') : [])
  const notices: ArmNotice[] = [
    ...Object.entries(pending)
      .filter(([siteId, at]) => keep.has(siteId) && !dismissed.has(dismissKey(siteId, at)))
      .map(([siteId, at]) => ({ kind: 'arming' as const, siteId, at })),
    ...[...disarmed].filter((siteId) => keep.has(siteId)).map((siteId) => ({ kind: 'disarmed' as const, siteId })),
  ]

  return { state, notices, busy, failures, reasons, setArmed, postpone, dismissFailure, dismiss, onArm, onArmPending, onArmPostponed, refresh }
}
